/* eslint-disable @typescript-eslint/no-inferrable-types */
import React, { useEffect, useState } from 'react';
import { PaperClipOutlined, UploadOutlined } from '@ant-design/icons';
import { Form, Row, Col, Card, Upload, Button, message } from 'antd';
import { uploadDocument } from '@app/api/export.api';

interface DocumentChecklistProps {
  editable: boolean;
  initialformFields: DocumentType[][] | DocumentType[];
  formFields: {
    name: string;
    document_id: string;
  }[];
  status: string | null;
  export_id: string | null;
  exportData: any;
  initialImportLinesDocFormFields?: DocumentType[];
  submittedDocs?: any;
  productNames: any;
  editableUser?: any;
}

interface DocumentType {
  image_url?: string;
  name: string;
  document_id: string;
  status: string;
  url?: string;
  catalog_product_id?: string;
  file?: string;
  referenceable_type?: string;
  referenceable_id?: number | string | null;
  document_url?: string;
}

const MODEL_PRODUCT_CATEGORY = 'App\\Models\\ProductCategory';
const MODEL_PRODUCT = 'App\\Models\\Product';

/**
 * Normalise a raw doc object into a DocumentType.
 *
 * catalog_product_id is set ONLY when the doc belongs to a Product key
 * (i.e. the key prefix is "Product|" or referenceable_type is MODEL_PRODUCT).
 * ProductCategory docs and ADDITIONAL docs must NOT get a catalog_product_id
 * so they render under the General section.
 */
const normaliseDoc = (doc: any): DocumentType => {
  const fileUrl = doc.document_url || doc.image_url || doc.url || undefined;

  // Determine whether this is a product-level doc using the key prefix first,
  // then fall back to referenceable_type. ProductCategory docs are always general.
  const predefinedKey: string | undefined = doc._predefinedKey;
  const isProductDoc =
    (predefinedKey !== undefined && predefinedKey.startsWith('Product|')) ||
    (!predefinedKey && doc.referenceable_type === MODEL_PRODUCT);

  const catalogProductId = isProductDoc ? String(doc.referenceable_id ?? doc.catalog_product_id ?? '') : undefined;

  return {
    ...doc,
    url: fileUrl,
    catalog_product_id: catalogProductId,
  };
};

/**
 * Parse the initial submittedDocs which arrive in the shape:
 * {
 *   ADDITIONAL: [...],
 *   PREDEFINED: {
 *     "ProductCategory|1": [...],
 *     "Product|1": [...],
 *   }
 * }
 *
 * ADDITIONAL docs → general (no catalog_product_id).
 * PREDEFINED docs → determined by key prefix.
 */
const parseInitialExportDocuments = (exportDocuments: any): DocumentType[] => {
  if (!exportDocuments) return [];

  const result: DocumentType[] = [];

  // ADDITIONAL docs are always general documents
  const additional: any[] = exportDocuments.ADDITIONAL ?? [];
  additional.forEach((doc) => {
    result.push(
      normaliseDoc({
        ...doc,
        // Explicitly clear any referenceable_type so normaliseDoc doesn't
        // accidentally treat them as product docs.
        referenceable_type: MODEL_PRODUCT_CATEGORY,
        referenceable_id: null,
        // No _predefinedKey → normaliseDoc will rely on referenceable_type,
        // which is ProductCategory → not a product doc → no catalog_product_id.
      }),
    );
  });

  // PREDEFINED: keys are like "ProductCategory|1" or "Product|1"
  const predefined: Record<string, any[]> = exportDocuments.PREDEFINED ?? {};
  Object.entries(predefined).forEach(([key, docs]) => {
    if (!Array.isArray(docs)) return;
    const [modelShortName, idStr] = key.split('|');
    const refType = modelShortName === 'Product' ? MODEL_PRODUCT : MODEL_PRODUCT_CATEGORY;
    docs.forEach((doc) => {
      const refId = doc.referenceable_id ?? (idStr ? Number(idStr) : null);
      result.push(
        normaliseDoc({
          ...doc,
          _predefinedKey: key, // ← carries the "Product|" vs "ProductCategory|" signal
          referenceable_type: refType,
          referenceable_id: refId,
        }),
      );
    });
  });

  return result;
};

/**
 * Parse the upload API response which arrives in the shape:
 * {
 *   "App\\Models\\ProductCategory": { "1": [...] },
 *   "App\\Models\\Product":         { "1": [...] },
 * }
 *
 * We convert the full class name back to the short prefix so that
 * normaliseDoc can use the same _predefinedKey logic.
 */
const parseUploadResponseDocuments = (exportDocuments: any): DocumentType[] => {
  if (!exportDocuments) return [];

  // Map full Laravel model class → short key prefix used in _predefinedKey
  const classToShortName: Record<string, string> = {
    [MODEL_PRODUCT]: 'Product',
    [MODEL_PRODUCT_CATEGORY]: 'ProductCategory',
  };

  const result: DocumentType[] = [];

  Object.entries(exportDocuments).forEach(([modelClass, idMap]: [string, any]) => {
    const shortName = classToShortName[modelClass] ?? modelClass;

    Object.entries(idMap).forEach(([id, docs]: [string, any]) => {
      if (!Array.isArray(docs)) return;
      const predefinedKey = `${shortName}|${id}`;

      docs.forEach((doc) => {
        result.push(
          normaliseDoc({
            ...doc,
            _predefinedKey: predefinedKey, // ← "Product|1" or "ProductCategory|1"
            referenceable_type: modelClass,
            referenceable_id: Number(id),
          }),
        );
      });
    });
  });

  return result;
};

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  editable,
  initialformFields: propInitialFields,
  status,
  export_id,
  initialImportLinesDocFormFields,
  productNames,
  submittedDocs,
  editableUser,
}) => {
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [documentFields, setDocumentFields] = useState<DocumentType[]>([]);
  const [fileList, setFileList] = useState<Record<string, any[]>>({});

  const canUpload = editable && status === 'PENDING_DOCUMENT' && editableUser;

  const getFileKey = (documentId: string, productId?: string | number | null) =>
    `${documentId}_${productId || export_id || 'general'}`;

  const resolveUrl = (doc: any): string | undefined => doc?.document_url || doc?.image_url || doc?.url || undefined;

  const buildFileListEntry = (doc: DocumentType) => {
    const fileUrl = resolveUrl(doc);
    if (!fileUrl) return null;
    const fileKey = getFileKey(doc.document_id, doc.catalog_product_id);
    return {
      key: fileKey,
      entry: [{ uid: fileKey, name: doc.file || doc.name, status: 'done', url: fileUrl }],
    };
  };

  useEffect(() => {
    // Step 1 — Seed the map with field-slot definitions (these define WHICH cards
    // are rendered; they may arrive without catalog_product_id / url).
    // We normalise them so that any referenceable_type / _predefinedKey hint on
    // them is honoured, and so their map key matches what submitted docs will use.
    const mergedMap = new Map<string, DocumentType>();

    const predefinedFields = Array.isArray(propInitialFields?.[0])
      ? (propInitialFields as DocumentType[][]).flat()
      : (propInitialFields as DocumentType[]);

    predefinedFields?.forEach((raw) => {
      const doc = normaliseDoc(raw);
      const key = getFileKey(doc.document_id, doc.catalog_product_id);
      mergedMap.set(key, { ...doc, url: resolveUrl(doc) });
    });

    // initialImportLinesDocFormFields carry the product-based slot definitions.
    // They MUST be normalised here so their catalog_product_id (derived from
    // referenceable_type/referenceable_id or _predefinedKey) is set correctly,
    // which in turn makes getFileKey produce the right key for them.
    initialImportLinesDocFormFields
      ?.filter((raw): raw is DocumentType => !!raw)
      .forEach((raw) => {
        const doc = normaliseDoc(raw);
        const key = getFileKey(doc.document_id, doc.catalog_product_id);
        mergedMap.set(key, { ...doc, url: resolveUrl(doc) });
      });

    // Step 2 — Overlay submitted (already-uploaded) docs on top of the slots.
    // submittedDocs may be:
    //   • the full export_documents object  { ADDITIONAL: [...], PREDEFINED: {...} }
    //   • just the PREDEFINED slice         { "ProductCategory|1": [...], "Product|1": [...] }
    // Wrap the PREDEFINED-only shape so parseInitialExportDocuments handles both.
    let normalizedSubmitted = submittedDocs;
    if (normalizedSubmitted && !normalizedSubmitted.ADDITIONAL && !normalizedSubmitted.PREDEFINED) {
      normalizedSubmitted = { ADDITIONAL: [], PREDEFINED: normalizedSubmitted };
    }

    const parsedSubmitted = parseInitialExportDocuments(normalizedSubmitted);
    parsedSubmitted.forEach((doc) => {
      // Use the same key derivation — catalog_product_id is already set correctly
      // by normaliseDoc inside parseInitialExportDocuments.
      const key = getFileKey(doc.document_id, doc.catalog_product_id);
      mergedMap.set(key, { ...doc, url: resolveUrl(doc) });
    });

    const mergedDocs = Array.from(mergedMap.values());
    setDocumentFields(mergedDocs);

    const initialFiles: Record<string, any[]> = {};
    mergedDocs.forEach((doc) => {
      const result = buildFileListEntry(doc);
      if (result) initialFiles[result.key] = result.entry;
    });
    setFileList(initialFiles);
  }, [propInitialFields, submittedDocs, initialImportLinesDocFormFields, export_id]);

  const processUploadResponse = (response: any) => {
    const exportDocuments = response?.data?.export_documents;
    if (!exportDocuments) return;

    const responseDocs = parseUploadResponseDocuments(exportDocuments);
    if (responseDocs.length === 0) return;

    setDocumentFields((prev) => {
      const map = new Map<string, DocumentType>();
      prev.forEach((doc) => map.set(getFileKey(doc.document_id, doc.catalog_product_id), doc));
      responseDocs.forEach((doc) => {
        map.set(getFileKey(doc.document_id, doc.catalog_product_id), {
          ...doc,
          url: resolveUrl(doc),
        });
      });
      return Array.from(map.values());
    });

    setFileList((prev) => {
      const updated = { ...prev };
      responseDocs.forEach((doc) => {
        const result = buildFileListEntry(doc);
        if (result) updated[result.key] = result.entry;
      });
      return updated;
    });
  };

  const handleUpload = async (documentId: string, file: File, productId: string) => {
    if (!export_id) {
      message.error('Export ID is required');
      return;
    }

    const uploadKey = getFileKey(documentId, productId);

    try {
      setUploading((prev) => ({ ...prev, [uploadKey]: true }));

      const formData = new FormData();
      formData.append('document_id', documentId);
      formData.append('file', file, file.name);
      formData.append('export_id', export_id);

      if (productId && productId !== export_id) {
        formData.append('catalog_product_id', productId);
      }

      const response = await uploadDocument(Number(export_id), formData);
      processUploadResponse(response);
      message.success(`${file.name} uploaded successfully`);
    } catch (error) {
      console.error('Upload error:', error);
      message.error(`Failed to upload ${file.name}`);
    } finally {
      setUploading((prev) => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleChange = async (documentId: string, file: any, productId: string) => {
    const newFiles = file.fileList.filter((f: any) => f.originFileObj);
    const fileKey = getFileKey(documentId, productId);

    if (newFiles.length > 0) {
      const latestFile = newFiles[newFiles.length - 1];
      setFileList((prev) => ({ ...prev, [fileKey]: [latestFile] }));
      await handleUpload(documentId, latestFile.originFileObj, productId);
    } else {
      message.error('No file detected, please try again.');
    }
  };

  const renderFileLink = (fileUrl: string | undefined, fileName: string | undefined) => {
    if (fileUrl) {
      return (
        <a
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 text-xs truncate hover:underline"
          title={fileName}
        >
          {fileName}
        </a>
      );
    }
    return (
      <span className="text-gray-600 text-xs truncate" title={fileName}>
        {fileName}
      </span>
    );
  };

  const renderUploadCard = (docName: string, documentId: string, productId: string) => {
    const fileKey = getFileKey(documentId, productId);
    const isUploading = uploading[fileKey] || false;
    const currentFiles = (fileList[fileKey] || []).slice(0, 1);
    const hasFile = currentFiles.length > 0;
    const fileEntry = hasFile ? currentFiles[0] : null;
    const fileUrl = fileEntry?.url as string | undefined;
    const fileName = fileEntry?.name as string | undefined;

    return (
      <Col xs={24} sm={12} key={fileKey}>
        <Card className="bg-white shadow-sm hover:shadow-md transition-shadow duration-200" style={{ minHeight: 160 }}>
          <div className="flex flex-col gap-3">
            <span className="text-gray-700 font-medium text-sm">{docName}</span>
            <div
              className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex flex-col gap-3"
              style={{ minHeight: 90 }}
            >
              <Upload
                accept=".pdf,.jpg,.jpeg,.png"
                beforeUpload={() => false}
                fileList={currentFiles}
                onChange={(file) => handleChange(documentId, file, productId)}
                showUploadList={false}
                className="w-full"
                disabled={!canUpload}
              >
                <Button size="small" disabled={isUploading || !canUpload} style={{ marginTop: '10px' }}>
                  <UploadOutlined className="text-gray-500" />
                  <span>{isUploading ? 'Uploading...' : hasFile ? 'Re-Upload' : 'Upload'}</span>
                </Button>
              </Upload>
              {hasFile && fileEntry && (
                <div className="flex flex-col gap-2" style={{ marginTop: '10px' }}>
                  <div className="flex items-center gap-1 min-w-0">
                    <PaperClipOutlined className="text-gray-400 flex-shrink-0 text-xs" />
                    {renderFileLink(fileUrl, fileName)}
                  </div>
                  <div className="text-green-500 text-sm">✓ File uploaded</div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </Col>
    );
  };

  const renderDocumentFields = () => {
    const docs = documentFields.filter((doc) => !doc.catalog_product_id);

    return (
      <>
        <h3 className="text-lg font-semibold mb-6">General Document Checklist</h3>
        {docs.length > 0 ? (
          <Row gutter={[16, 16]}>{docs.map((doc) => renderUploadCard(doc.name, doc.document_id, export_id || ''))}</Row>
        ) : (
          <Col span={24}>
            <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">
              No documents available for this category.
            </div>
          </Col>
        )}
      </>
    );
  };

  const renderProductBasedDocuments = () => {
    const productDocs = documentFields.filter((doc) => !!doc.catalog_product_id);

    const grouped = productDocs.reduce<Record<string, DocumentType[]>>((acc, doc) => {
      const pid = doc.catalog_product_id!;
      if (!acc[pid]) acc[pid] = [];
      acc[pid].push(doc);
      return acc;
    }, {});

    return (
      <>
        <br />
        <h3 className="text-lg font-semibold mb-4 mt-10">Product Based File Upload</h3>
        {Object.keys(grouped).length > 0 ? (
          Object.entries(grouped).map(([productId, docs]) => (
            <div key={`product-${productId}`} className="mb-8">
              <h3 className="text-base font-medium mb-2">
                {productNames?.[productId] ?? productNames ?? `Product #${productId}`}
              </h3>
              <Row gutter={[16, 16]}>
                {docs.map((doc) => renderUploadCard(doc.name, doc.document_id, doc.catalog_product_id!))}
              </Row>
            </div>
          ))
        ) : (
          <Col span={24}>
            <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">
              No documents available for this product.
            </div>
          </Col>
        )}
      </>
    );
  };

  return (
    <div className="p-6 bg-gray-50 rounded-lg">
      <Form layout="vertical">
        {renderDocumentFields()}
        {renderProductBasedDocuments()}
      </Form>
    </div>
  );
};

export default DocumentChecklist;
